import { useEffect } from 'react';
import includes from 'lodash/includes';
import toUpper from 'lodash/toUpper';
import snakeCase from 'lodash/snakeCase';

import { IconsEnum } from '../../../../../../../assets/icons/types';
import {
  ID,
  FetchItemsFilterItems,
  FetchItemsSort,
  FetchItemsSortableField
} from '../../../../../../../types';
import { CompactIndexTableHeaderSortFilterCellFilterField } from '../../CompactIndexTableHeaderSortFilterCell.types';

import { usePopoverState } from '../../../../../../../helpers/Popover/hooks/usePopoverState';

import { useUniversalFilterLocalState } from '../../../../../../../helpers/filters/UniversalFilter';
import { usePreviousValue } from '../../../../../../../common/hooks/usePreviousValue';

interface CompactIndexTableHeaderSortFilterCellOptions<FiltersType> {
  viewId: ID;
  filterFields: CompactIndexTableHeaderSortFilterCellFilterField<FiltersType>[];
  currentFilters?: FiltersType;
  currentSort?: FetchItemsSort;
  afterClear?: () => void;
  onFilter: FetchItemsFilterItems<Partial<FiltersType>>;
  sortableField?: FetchItemsSortableField;
  ascField?: string;
  descField?: string;
}

function useCompactIndexTableHeaderSortFilterCell<
  FiltersType extends Record<string, any>
>({
  viewId,
  filterFields,
  currentFilters,
  currentSort,
  afterClear,
  onFilter,
  sortableField,
  ascField,
  descField
}: CompactIndexTableHeaderSortFilterCellOptions<FiltersType>) {
  const upCasedField = toUpper(snakeCase(sortableField));

  const ascSort = ascField || `${upCasedField}_ASC`;

  const descSort = descField || `${upCasedField}_DESC`;

  const isSortedAsc = includes(currentSort, ascSort);

  const isSortedDesc = includes(currentSort, descSort);

  const sortIcon = isSortedDesc
    ? IconsEnum.ARROW_NARROW_UP_SOLID
    : IconsEnum.ARROW_NARROW_DOWN_SOLID;

  const {
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover
  } = usePopoverState();

  const {
    isFiltered,
    localFiltersState,
    setLocalFiltersState,
    handleApply,
    handleClear
  } = useUniversalFilterLocalState({
    viewId,
    filterFields,
    currentFilters,
    onFilter,
    afterClear: closePopover,
    afterApply: closePopover
  });

  const previousIsFiltered = usePreviousValue(isFiltered);

  useEffect(() => {
    if (!isFiltered && previousIsFiltered) {
      afterClear?.();
    }
  }, [afterClear, isFiltered, previousIsFiltered]);

  return {
    ascSort,
    descSort,
    isSortedAsc,
    isSortedDesc,
    sortIcon,
    isFiltered,
    localFiltersState,
    setLocalFiltersState,
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover,
    handleApply,
    handleClear
  };
}

export default useCompactIndexTableHeaderSortFilterCell;
